/**
 * Questionnaire Module Constants
 *
 * Configuration values and constants used throughout the questionnaire module
 */

/**
 * Rating scale configuration (1-5 scale)
 */
export const RATING_SCALE = {
  MIN: 1,
  MAX: 5,
  DEFAULT: 3,
} as const;

/**
 * Rating scale labels for accessibility and display
 */
export const RATING_LABELS: Record<number, string> = {
  1: "Strongly Disagree / Not at All",
  2: "Disagree / Minimal",
  3: "Neutral / Moderate",
  4: "Agree / Significant",
  5: "Strongly Agree / Comprehensive",
};

/**
 * Score interpretation thresholds
 */
export const SCORE_THRESHOLDS = {
  CRITICAL: 40,
  LOW: 55,
  MODERATE: 70,
  GOOD: 85,
  EXCELLENT: 100,
} as const;

/**
 * Score level descriptions
 */
export const SCORE_LEVELS = {
  CRITICAL: {
    label: "Critical",
    color: "#DC2626",
    description: "Immediate action required",
  },
  LOW: {
    label: "Low Readiness",
    color: "#F59E0B",
    description: "Significant gaps to address",
  },
  MODERATE: {
    label: "Moderate Readiness",
    color: "#EAB308",
    description: "Foundation exists, improvements needed",
  },
  GOOD: {
    label: "Good Readiness",
    color: "#10B981",
    description: "Well positioned for AI implementation",
  },
  EXCELLENT: {
    label: "Excellent Readiness",
    color: "#059669",
    description: "Optimal conditions for AI success",
  },
} as const;

/**
 * Local storage keys for progress persistence
 */
export const STORAGE_KEYS = {
  SESSION_ID: "questionnaire_session_id",
  RESPONSES: "questionnaire_responses",
  CURRENT_CATEGORY: "questionnaire_current_category",
  STARTED_AT: "questionnaire_started_at",
} as const;

/**
 * Auto-save configuration
 */
export const AUTO_SAVE = {
  ENABLED: true,
  DEBOUNCE_MS: 2000, // Wait 2 seconds after last change before saving
  MIN_RESPONSES: 5, // Minimum responses before auto-save kicks in
} as const;

/**
 * Validation rules
 */
export const VALIDATION = {
  REQUIRE_ALL_QUESTIONS: true,
  MIN_COMPLETION_PERCENTAGE: 90, // Must answer at least 90% to submit
  ALLOW_SKIP: false, // Can users skip questions?
} as const;

/**
 * UI configuration
 */
export const UI_CONFIG = {
  QUESTIONS_PER_PAGE: "category", // Show one category at a time
  SHOW_PROGRESS_BAR: true,
  SHOW_CATEGORY_SCORES: false, // Don't show scores until completion
  ENABLE_NAVIGATION: true,
  SHOW_EXAMPLES: true,
  COMPACT_MODE: false,
} as const;

/**
 * Animation durations (ms)
 */
export const ANIMATIONS = {
  PAGE_TRANSITION: 300,
  BUTTON_HOVER: 150,
  SCORE_REVEAL: 500,
  PROGRESS_UPDATE: 400,
} as const;

/**
 * Keyboard shortcuts
 */
export const SHORTCUTS = {
  NEXT_CATEGORY: "ArrowRight",
  PREV_CATEGORY: "ArrowLeft",
  SUBMIT: "Enter",
  CANCEL: "Escape",
} as const;

/**
 * Category icons mapping (using common icon names)
 */
export const CATEGORY_ICONS: Record<string, string> = {
  "Data Infrastructure & Quality": "database",
  "Organizational Culture & Change Readiness": "users",
  "Technical Capabilities & IT Infrastructure": "server",
  "Strategic Alignment & Use-Case Clarity": "target",
  "Compliance, Governance & Risk Management": "shield",
  "Current Technology Stack & Vendor Ecosystem": "layers",
  "Investment Optimization Opportunities": "trending-up",
};

/**
 * Layer colors for visual distinction
 */
export const LAYER_COLORS = {
  "Structural Readiness": "#3B82F6", // Blue
  "Investment Leverage": "#8B5CF6", // Purple
};
